import { useEffect, useState } from "react";
import { getMarketplaceClient } from "../lib/marketplace-client";
import { filterPlugins, extractCategories } from "../lib/search";
import { reportError } from "../lib/newrelic";
import FilterBar from "../components/FilterBar";
import PluginCard from "../components/PluginCard";
import PluginListView from "../components/PluginListView";
import CatalogToolbar from "../components/CatalogToolbar";
import type { SortOption, ViewMode } from "../components/CatalogToolbar";
import SearchBar from "../components/SearchBar";
import Loading from "../components/Loading";
import GettingStarted from "../components/GettingStarted";
import type { ExtensionType, MarketplaceEntry, SearchState, SupportedClient } from "../types/index";

const CLIENT_FILTER_OPTIONS: { value: SupportedClient; label: string }[] = [
  { value: "claude-chat",   label: "Claude Chat" },
  { value: "claude-cowork", label: "Claude Cowork" },
  { value: "claude-code",   label: "Claude Code" },
  { value: "copilot",       label: "GitHub Copilot" },
];

type TypeFilter = "all" | ExtensionType;

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all",    label: "All" },
  { value: "plugin", label: "Plugins" },
  { value: "skill",  label: "Skills" },
];

const SORT_ORDER_MAP: Record<SortOption, "asc" | "desc"> = {
  name: "asc",
  updated: "desc",
  created: "desc",
  official: "desc", // ignored by the official sort (Official-first is a fixed order)
};

function getEmptyStateMessage(
  query: string,
  clients: SupportedClient[],
  categories: string[],
  typeFilter: TypeFilter = "all",
  officialOnly = false,
): string {
  const noun =
    typeFilter === "plugin" ? "plug-ins"
    : typeFilter === "skill" ? "skills"
    : "plug-ins or skills";
  const parts: string[] = [];
  if (query) parts.push(`matching "${query}"`);
  if (officialOnly) parts.push("marked Official CAI");
  if (clients.length > 0) {
    const labels = clients.map((c) => CLIENT_FILTER_OPTIONS.find((o) => o.value === c)?.label ?? c);
    parts.push(`for ${labels.join(" or ")}`);
  }
  if (categories.length === 1) parts.push(`in ${categories[0]}`);
  else if (categories.length > 1) parts.push(`in ${categories.join(" or ")}`);
  return parts.length > 0 ? `No ${noun} ${parts.join(" ")}` : `No ${noun} available`;
}

interface CatalogPageProps {
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
  initialClients?: string[];
  initialCategories?: string[];
  initialQuery?: string;
  initialType?: ExtensionType;
  initialOfficial?: boolean;
  onHelp?: () => void;
  onFiltersChange?: (query: string, clients: SupportedClient[], categories: string[], type?: ExtensionType, official?: boolean) => void;
}

export default function CatalogPage({ onSelectPlugin, initialClients, initialCategories, initialQuery, initialType, initialOfficial, onHelp, onFiltersChange }: CatalogPageProps) {
  const [state, setState] = useState<SearchState>({
    query: initialQuery ?? "",
    results: [],
    isLoading: true,
    error: null,
  });
  const [allPlugins, setAllPlugins] = useState<MarketplaceEntry[]>([]);
  const [clientFilters, setClientFilters] = useState<SupportedClient[]>((initialClients as SupportedClient[]) ?? []);
  const [categoryFilters, setCategoryFilters] = useState<string[]>(initialCategories ?? []);
  const [searchQuery, setSearchQuery] = useState(initialQuery ?? "");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(initialType ?? "all");
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("name");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [officialOnly, setOfficialOnly] = useState(initialOfficial ?? false);

  const applyFilters = (
    query: string,
    clients: SupportedClient[],
    categories: string[],
    type: TypeFilter = typeFilter,
    sort: SortOption = sortBy,
    official: boolean = officialOnly,
    plugins: MarketplaceEntry[] = allPlugins,
  ) => {
    setState((s) => ({
      ...s,
      query,
      results: filterPlugins(plugins, {
        query,
        supportedClients: clients.length > 0 ? clients : undefined,
        categories: categories.length > 0 ? categories : undefined,
        extensionType: type === "all" ? undefined : type,
        officialOnly: official,
        sortBy: sort,
        sortOrder: SORT_ORDER_MAP[sort],
      }),
    }));
    onFiltersChange?.(query, clients, categories, type === "all" ? undefined : type, official);
  };

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const client = getMarketplaceClient();
        const catalog = await client.getCatalog();
        const sorted = [...catalog.plugins].sort((a, b) =>
          a.pluginName.toLowerCase().localeCompare(b.pluginName.toLowerCase()),
        );
        setAllPlugins(sorted);
        setAvailableCategories(extractCategories(sorted));
        const initClients = (initialClients as SupportedClient[]) ?? [];
        const initCategories = initialCategories ?? [];
        const initQuery = initialQuery ?? "";
        setState((s) => ({
          ...s,
          results: filterPlugins(sorted, {
            query: initQuery,
            supportedClients: initClients.length > 0 ? initClients : undefined,
            categories: initCategories.length > 0 ? initCategories : undefined,
            extensionType: initialType,
            officialOnly: initialOfficial ?? false,
            sortBy: "name",
            sortOrder: "asc",
          }),
          isLoading: false,
        }));
      } catch (error) {
        console.error("Failed to load catalog:", error);
        reportError(error, "catalog-fetch-page");
        setState((s) => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : "Failed to load catalog",
        }));
      }
    };

    loadCatalog();
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    applyFilters(query, clientFilters, categoryFilters);
  };

  const toggleFilter = <T,>(prev: T[], value: T, eq: (a: T, b: T) => boolean): T[] =>
    prev.some((v) => eq(v, value)) ? prev.filter((v) => !eq(v, value)) : [...prev, value];

  const handleClientToggle = (client: SupportedClient) => {
    const next = toggleFilter(clientFilters, client, (a, b) => a === b);
    setClientFilters(next);
    applyFilters(state.query, next, categoryFilters);
  };

  const handleCategoryToggle = (category: string) => {
    const next = toggleFilter(categoryFilters, category, (a, b) => a.toLowerCase() === b.toLowerCase());
    setCategoryFilters(next);
    applyFilters(state.query, clientFilters, next);
  };

  const handleTypeChange = (type: TypeFilter) => {
    setTypeFilter(type);
    applyFilters(state.query, clientFilters, categoryFilters, type);
  };

  const handleSortChange = (sort: SortOption) => {
    setSortBy(sort);
    applyFilters(state.query, clientFilters, categoryFilters, typeFilter, sort);
  };

  const handleOfficialToggle = () => {
    const next = !officialOnly;
    setOfficialOnly(next);
    applyFilters(state.query, clientFilters, categoryFilters, typeFilter, sortBy, next);
  };

  if (state.error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h1>Error Loading Marketplace</h1>
        <p style={{ color: "red" }}>{state.error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.5rem 1rem",
            fontSize: "1rem",
            backgroundColor: "#0078d4",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "2rem" }}>
        <h1>AI Marketplace</h1>
        <p style={{ color: "#666", marginBottom: "1rem" }}>
          Browse and discover available AI plug-ins and skills for Cox Auto approved LLM clients.
        </p>
      </div>

      <GettingStarted onHelp={onHelp} />
      <SearchBar onSearch={handleSearch} isLoading={state.isLoading} externalQuery={searchQuery} placeholder="Search plug-ins and skills..." />
      <p style={{ color: "#999", fontSize: "0.9rem", margin: "-1rem 0 1.5rem" }}>
        Total available: {allPlugins.filter((p) => p.extensionType === "skill").length} skills · {allPlugins.filter((p) => p.extensionType === "plugin").length} plug-ins
      </p>

      <CatalogToolbar
        sortBy={sortBy}
        onSortChange={handleSortChange}
        viewMode={viewMode}
        onViewChange={setViewMode}
        officialOnly={officialOnly}
        onOfficialToggle={handleOfficialToggle}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#888", minWidth: "6rem" }}>
            Type:
          </span>
          <div role="group" aria-label="Filter by type" style={{ display: "inline-flex", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden" }}>
            {TYPE_FILTER_OPTIONS.map((opt, i) => {
              const active = typeFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleTypeChange(opt.value)}
                  aria-pressed={active}
                  style={{
                    padding: "0.45rem 0.9rem",
                    fontSize: "0.85rem",
                    fontWeight: active ? 600 : 400,
                    color: active ? "#0078d4" : "#666",
                    backgroundColor: active ? "#e6f0ff" : "#fff",
                    border: "none",
                    borderLeft: i > 0 ? "1px solid #d1d5db" : "none",
                    outline: active ? "2px solid #0078d4" : "none",
                    outlineOffset: "-2px",
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#888", minWidth: "6rem" }}>
            Works with:
          </span>
          <div role="group" aria-label="Filter by client" style={{ display: "inline-flex", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden" }}>
            {CLIENT_FILTER_OPTIONS.map((opt, i) => {
              const active = clientFilters.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => handleClientToggle(opt.value)}
                  aria-pressed={active}
                  style={{
                    padding: "0.45rem 0.9rem",
                    fontSize: "0.85rem",
                    fontWeight: active ? 600 : 400,
                    color: active ? "#0078d4" : "#666",
                    backgroundColor: active ? "#e6f0ff" : "#fff",
                    border: "none",
                    borderLeft: i > 0 ? "1px solid #d1d5db" : "none",
                    outline: active ? "2px solid #0078d4" : "none",
                    outlineOffset: "-2px",
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <FilterBar
          categories={availableCategories}
          activeCategories={categoryFilters}
          onCategoryToggle={handleCategoryToggle}
          labelMinWidth="6rem"
        />
      </CatalogToolbar>

      {state.isLoading && !allPlugins.length ? (
        <Loading message="Loading catalog..." />
      ) : state.results.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p style={{ fontSize: "1.1rem", color: "#666" }}>
            {getEmptyStateMessage(state.query, clientFilters, categoryFilters, typeFilter, officialOnly)}
          </p>
          {(state.query || clientFilters.length > 0 || categoryFilters.length > 0 || typeFilter !== "all" || officialOnly) && (
            <button
              onClick={() => {
                setClientFilters([]);
                setCategoryFilters([]);
                setSearchQuery("");
                setTypeFilter("all");
                setOfficialOnly(false);
                applyFilters("", [], [], "all", sortBy, false);
              }}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#f0f0f0",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : viewMode === "list" ? (
        <PluginListView plugins={state.results} onSelectPlugin={onSelectPlugin} />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {state.results.map((plugin) => (
            <PluginCard
              key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
              plugin={plugin}
              onClick={() => onSelectPlugin(plugin)}
              onClientClick={handleClientToggle}
              onCategoryClick={handleCategoryToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
